import type { KeyboardEvent, ReactNode } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import get from 'lodash/get';

import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import { searchSelectOptions } from '@tmh-website/services/search.service';
import { Color } from '@tmh-website/shared/colors';
import DropdownMenu from '@tmh-website/ui/DropdownMenu';
import DropdownMenuItem from '@tmh-website/ui/DropdownMenuItem';
import {
  FormElementCommonProps,
  FormElementThemeColor,
  SelectOption,
  SelectSearch,
  StyledFormElements,
  formElementThemes,
} from '@tmh-website/ui/FormElements';
import MultiselectEntry from '@tmh-website/ui/MultiselectEntry';

import * as Styled from './Multiselect.styles';

export interface MultiselectProps extends Omit<FormElementCommonProps, 'themeColor'> {
  options: SelectOption[];
  value?: string[];
  helpText?: string | ReactNode;
  themeColor: FormElementThemeColor;
  smallLabel?: boolean;
  search?: SelectSearch;
  required?: boolean;
  placeholder?: string;
}

const Multiselect = ({
  themeColor = Color.Neutral_0_White,
  helpText,
  label,
  name,
  disabled,
  options,
  placeholder,
  value,
  search = false,
  required = false,
  smallLabel = false,
}: MultiselectProps) => {
  const { t } = useTranslation();
  const uniqueId = useId();
  const nameWithId = name + '_' + uniqueId;
  const { errors, touched, values, setFieldValue } = useFormikContext<any>();
  const hasError = get(errors, name) && get(touched, name);
  const fieldValues = (get(values, name) as string[]) ?? [];
  const [isVisible, setIsVisible] = useState(false);
  const [activeSearchFilter, setActiveSearchFilter] = useState('');

  const hasLabelForFalsyValue = options.some(option => !option.value);
  const showPlaceholder = !value && !!placeholder;
  const checkedEntries = fieldValues.length;

  let filteredOptions = options;

  if (search && activeSearchFilter.trim() !== '') {
    if (typeof search === 'function') {
      filteredOptions = search(options, activeSearchFilter);
    } else {
      filteredOptions = searchSelectOptions(options, activeSearchFilter, search);
    }
  }

  const visibleLabel = showPlaceholder
    ? placeholder
    : fieldValues.map(value => options.find(option => option.value === value)?.label).join(', ');

  return (
    <div>
      <StyledFormElements.Container
        $themeColor={themeColor}
        $hasError={hasError}
        $isDisabled={disabled}
        $disableSmallLabelOnFocus>
        {label && (
          <StyledFormElements.InputLabel
            $themeColor={themeColor}
            $fieldIsFilled={checkedEntries > 0 || smallLabel || showPlaceholder || hasLabelForFalsyValue}
            htmlFor={nameWithId}>
            {label}
            {required && '*'}
          </StyledFormElements.InputLabel>
        )}
        <DropdownMenu
          themeColor={formElementThemes[themeColor].dropdownThemeColor}
          closeOnClick={false}
          hasSearchInput={search !== false}
          onSearchInputChange={setActiveSearchFilter}
          disabled={disabled}
          customOffset={6}
          customContainerProps={{ id: nameWithId }}
          anchorEl={
            <Styled.MultiselectButton
              as='button'
              type='button'
              onKeyUp={(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  setIsVisible(!isVisible);
                }
              }}
              onClick={() => {
                setIsVisible(!isVisible);
              }}
              $themeColor={themeColor}
              disabled={disabled}
              aria-label={visibleLabel ? undefined : t('global:pleaseChoose')}>
              <Styled.FakeValue $themeColor={themeColor} $isPlaceholder={!!showPlaceholder}>
                {visibleLabel}
              </Styled.FakeValue>
              <Styled.Number $themeColor={themeColor} $isVisible={checkedEntries > 0}>
                {Math.max(checkedEntries, 1)}
              </Styled.Number>
            </Styled.MultiselectButton>
          }>
          {filteredOptions.map(option => (
            <DropdownMenuItem
              key={option.value.toString()}
              themeColor={formElementThemes[themeColor].dropdownThemeColor}
              disabled={option.disabled}
              onSelect={() => {
                const fieldValues: SelectOption['value'][] = get(values, name) ?? [];
                setFieldValue(
                  name,
                  fieldValues.includes(option.value)
                    ? fieldValues.filter(entry => entry !== option.value)
                    : [...fieldValues, option.value]
                );
              }}>
              <MultiselectEntry
                themeColor={themeColor}
                {...option}
                checked={fieldValues.includes(option.value as string)}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </StyledFormElements.Container>
      {helpText && <StyledFormElements.HelpText $themeColor={themeColor}>{helpText}</StyledFormElements.HelpText>}
      {hasError && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
    </div>
  );
};

export default Multiselect;
